import './MetadataSettings.css'

import cn from 'clsx'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import React, { Component } from 'react'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import type { TrialLeft } from '@disco/utils/trial'

import type { UserAliasSettings } from 'types/user-settings'

import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ChoiceList from 'components/ui/choice-list'
import ContactUsLink from 'components/ui/contact-us-link'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import PageContainer from 'components/ui/page-container'
import TextField from 'components/ui/text-field'

type Props = {
  updateUserSettings: (...args: Array<any>) => any
  aliases: UserAliasSettings
  updating: boolean
  sendMixpanelEvent: typeof sendMixpanelEvent
  trialLeft?: TrialLeft | null | undefined
  isTrial: boolean
  isFeatureEnabled?: boolean
  aliasCommentsEnabled?: boolean
}

class MetadataSettings extends Component<
  Props,
  {
    aliases: Record<string, any>
  }
> {
  constructor(props: Props) {
    super(props)
    this.state = {
      aliases: { ...this.props.aliases },
    }
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open metadata user settings')
  }

  _onOptionToggle: (value: string) => void = (value: string) => {
    // X-Disco-StripUnknown is unique thing, default value should be false
    const aliasOptionDefaultValue = value !== 'X-Disco-StripUnknown'
    this.setState({
      aliases: {
        ...this.state.aliases,
        [value]: {
          include: !get(
            this.state.aliases,
            `${value}.include`,
            aliasOptionDefaultValue,
          ),
          value: '',
        },
      },
    })
  }
  _onSettingsUpdate: () => void = () => {
    this.props.updateUserSettings(
      {
        metadata_aliases: this.state.aliases,
      },
      true,
    )
    this.props.sendMixpanelEvent('Save metadata user settings')
    const newComment = get(
      this.state.aliases,
      '[X-Disco-UserExtraComment.value]',
      '',
    )

    if (
      get(this.props.aliases, '[X-Disco-UserExtraComment.value]', '') !==
        newComment &&
      newComment !== ''
    ) {
      this.props.sendMixpanelEvent(
        'Save custom message in metadata user settings',
      )
    }

    const propsCheckboxes = omit(this.props.aliases, 'X-Disco-UserExtraComment')
    const stateCheckboxes = omit(this.state.aliases, 'X-Disco-UserExtraComment')

    if (
      !isEqual(propsCheckboxes, stateCheckboxes) &&
      Object.keys(stateCheckboxes).some(
        key =>
          stateCheckboxes[key].include === (key === 'X-Disco-StripUnknown'),
      )
    ) {
      this.props.sendMixpanelEvent(
        'Switch off fields in metadata user settings',
      )
    }
  }
  _onCommentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.props.isFeatureEnabled && !this.props.aliasCommentsEnabled) return
    let aliases: UserAliasSettings = {
      ...this.state.aliases,
      ['X-Disco-UserExtraComment']: {
        include: true,
        value: e.target.value,
      },
    }

    if (aliases['X-Disco-UserExtraComment'].value === '') {
      aliases = omit(aliases, 'X-Disco-UserExtraComment')
    }

    this.setState({
      aliases,
    })
  }

  render(): React.ReactNode {
    const { isTrial, trialLeft, isFeatureEnabled, aliasCommentsEnabled } =
      this.props
    return (
      <PageContainer>
        <DocumentHeadTags title="Settings | Metadata | DISCO" />
        {!isFeatureEnabled && !aliasCommentsEnabled && (
          <div className="AliasSettings_Alert">
            <Alert icon="warning" status="warning">
              With DISCO, you can manage your metadata via spreadsheet, and set
              automatic metadata settings to control which fields are populated
              on the lists you send out. You can also include custom messages,
              like your contact details, in the metadata of all your tracks with
              a couple of clicks.
              <br />
              <ContactUsLink intercomMsg="I’d like to upgrade to Plus to access playlist alias metadata functionality.">
                To access these features, please upgrade.
              </ContactUsLink>
            </Alert>
          </div>
        )}
        <div
          className={cn({
            AliasSettings_TextDisabled:
              !isFeatureEnabled && !aliasCommentsEnabled,
          })}
        >
          <Row columnsSize={[12]}>
            <div>
              <Heading size={500}>
                {aliasCommentsEnabled
                  ? 'Custom message on downloaded tracks'
                  : 'Manage metadata via spreadsheet'}
              </Heading>
              {(isFeatureEnabled || !aliasCommentsEnabled) && (
                <>
                  {isTrial && (
                    <div className="AliasSettings_AlertTrial">
                      <Alert icon="warning" status="warning">
                        <span>
                          Heads up!{' '}
                          <span>{trialLeft ? trialLeft.message : ''}</span>
                        </span>
                        <br />
                        <InlineLink to="/settings/billing">
                          Plus and Pro plans
                        </InlineLink>
                        <span>
                          &nbsp;allow unlimited metadata exports, and access to
                          Metadata settings.
                        </span>
                      </Alert>
                    </div>
                  )}
                  <div className="form-note">
                    We can export your DISCO metadata to spreadsheet, and you
                    can edit your metadata on the spreadsheet and we’ll
                    re-import it for you.{' '}
                    {isFeatureEnabled && (
                      <ContactUsLink>Contact us for your export</ContactUsLink>
                    )}
                  </div>
                </>
              )}
            </div>
          </Row>
          {(isFeatureEnabled || !aliasCommentsEnabled) && (
            <>
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <Heading size={500}>Metadata settings</Heading>
                </div>
              </Row>
              <Row columnsSize={[6, 6]}>
                <div>
                  <ChoiceList
                    title="Include the following metadata in the tracks I share"
                    tooltipId="tooltip_metadata_settings_include"
                    disabled={!isFeatureEnabled}
                    items={[
                      {
                        label: 'Artist',
                        value: 'Artist',
                        selected: get(
                          this.state.aliases,
                          'Artist.include',
                          true,
                        ),
                      },
                      {
                        label: 'Album',
                        value: 'Album',
                        selected: get(
                          this.state.aliases,
                          'Album.include',
                          true,
                        ),
                      },
                      {
                        label: 'Genre',
                        value: 'Genre',
                        selected: get(
                          this.state.aliases,
                          'Genre.include',
                          true,
                        ),
                      },
                      {
                        label: 'Composer',
                        value: 'Composer',
                        selected: get(
                          this.state.aliases,
                          'Composer.include',
                          true,
                        ),
                      },
                      {
                        label: 'Grouping',
                        value: 'Grouping',
                        selected: get(
                          this.state.aliases,
                          'Grouping.include',
                          true,
                        ),
                      },
                      {
                        label: 'Comments',
                        value: 'Comment',
                        selected: get(
                          this.state.aliases,
                          'Comment.include',
                          true,
                        ),
                      },
                    ]}
                    onChange={this._onOptionToggle}
                  />
                </div>
                <div>
                  <ChoiceList
                    multiple={false}
                    title="Include metadata that DISCO does not display"
                    tooltipId="tooltip_metadata_settings_strip_unknown_meta"
                    disabled={!isFeatureEnabled}
                    items={[
                      {
                        label: 'Yes',
                        value: false,
                      },
                      {
                        label: 'No',
                        value: true,
                      },
                    ]}
                    selected={get(
                      this.state.aliases,
                      `X-Disco-StripUnknown.include`,
                      false,
                    )}
                    onChange={() =>
                      this._onOptionToggle('X-Disco-StripUnknown')
                    }
                  />
                </div>
              </Row>
            </>
          )}
          <Row columnsSize={[12]}>
            <div>
              <TextField
                multiline={6}
                label="Enter a custom message in the Comments metadata"
                value={get(
                  this.state.aliases,
                  'X-Disco-UserExtraComment.value',
                  '',
                )}
                onChange={this._onCommentsChange}
                tooltipId="tooltip_metadata_settings_comments_text"
              />
            </div>
          </Row>
          <Button
            disabled={!isFeatureEnabled && !aliasCommentsEnabled}
            appearance="primary"
            caption="Update Metadata Settings"
            onClick={this._onSettingsUpdate}
            progressIndicator={this.props.updating}
            progressCaption="Updating Settings"
          />
        </div>
      </PageContainer>
    )
  }
}

export default MetadataSettings
